import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Icon, Input, Modal, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import type { Folder, VaultFile } from "../types";

/** Bytes as a person reads them. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FolderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4l2 2.5H19.5A1.5 1.5 0 0 1 21 9v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18V6.5Z" />
    </svg>
  );
}

export function VaultPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const toast = useToast();
  const canManage = can("tenant.manage");
  const [folders, setFolders] = useState<Folder[] | null>(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ mode: "create" | "rename" | "delete"; target?: Folder } | null>(null);
  const [files, setFiles] = useState<VaultFile[] | null>(null);
  const [filesError, setFilesError] = useState("");
  const [busy, setBusy] = useState(false);

  async function load(selectAfter?: string) {
    const rows = await endpoints.listFolders();
    setFolders(rows);
    if (selectAfter) setSelectedId(selectAfter);
    else if (!selectedId && rows.length) setSelectedId(rows.find((f) => !f.parentId)?.id ?? rows[0]!.id);
  }

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The documents in the selected folder. Cleared first so the previous
  // folder's contents are never shown under a new folder's name.
  useEffect(() => {
    if (!selectedId) return;
    let live = true;
    setFiles(null);
    setFilesError("");
    endpoints
      .listVaultFiles(selectedId)
      .then((rows) => { if (live) setFiles(rows); })
      .catch((e: unknown) => {
        if (live) setFilesError(e instanceof Error ? e.message : t("vault.loadFailed"));
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  async function reloadFiles() {
    if (!selectedId) return;
    setFiles(await endpoints.listVaultFiles(selectedId));
  }

  async function onUpload(file: File) {
    if (!selectedId) return;
    setBusy(true);
    try {
      await endpoints.uploadVaultFile(selectedId, file);
      await reloadFiles();
      toast.success(t("vault.uploaded"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t("vault.uploadFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveFile(f: VaultFile) {
    try {
      await endpoints.deleteVaultFile(f.id);
      await reloadFiles();
      toast.success(t("vault.removed"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  const byParent = useMemo(() => {
    const m = new Map<string | null, Folder[]>();
    for (const f of folders ?? []) {
      const arr = m.get(f.parentId) ?? [];
      arr.push(f);
      m.set(f.parentId, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.ord - b.ord || a.name.localeCompare(b.name));
    return m;
  }, [folders]);

  function toggle(id: string) {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  const selected = folders?.find((f) => f.id === selectedId) ?? null;

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!folders)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  function renderNode(node: Folder, depth: number) {
    const kids = byParent.get(node.id) ?? [];
    const isOpen = expanded.has(node.id);
    return (
      <div key={node.id}>
        <div
          className={node.id === selectedId ? "vtree__row is-sel" : "vtree__row"}
          style={{ paddingInlineStart: 8 + depth * 16 }}
        >
          <button
            className="vtree__tw"
            onClick={() => toggle(node.id)}
            aria-label="Toggle"
            style={{ visibility: kids.length ? "visible" : "hidden" }}
          >
            <Icon name={isOpen ? "chevronDown" : "chevronRight"} size={13} />
          </button>
          <button className="vtree__name" onClick={() => setSelectedId(node.id)}>
            <FolderIcon />
            <span>{node.name}</span>
            {node.system && (
              <span className="vtree__lock" title={t("vault.system")}>
                <Icon name="lock" size={12} />
              </span>
            )}
          </button>
        </div>
        {isOpen && kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  }

  async function submitCreate(name: string, parentId: string | null) {
    const f = await endpoints.createFolder({ parentId, name });
    if (parentId) setExpanded((s) => new Set(s).add(parentId));
    await load(f.id);
    toast.success(t("vault.created"));
  }
  async function submitRename(id: string, name: string) {
    await endpoints.renameFolder(id, name);
    await load(id);
    toast.success(t("vault.renamed"));
  }
  async function submitDelete(id: string) {
    await endpoints.deleteFolder(id);
    setSelectedId(null);
    await load();
    toast.success(t("vault.deleted"));
  }

  const crumbs = selected ? selected.path.split("/").filter(Boolean) : [];

  return (
    <div className="page">
      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("vault.title")}</h1>
          <p className="page__sub">{t("vault.subtitle")}</p>
        </div>
        {canManage && (
          <Button onClick={() => setDialog({ mode: "create" })}>{t("vault.newFolder")}</Button>
        )}
      </header>

      <div className="vault">
        <Card className="vault__tree" flush>
          <div className="vtree">{(byParent.get(null) ?? []).map((n) => renderNode(n, 0))}</div>
        </Card>

        <Card className="vault__detail">
          {selected ? (
            <>
              <div className="vault__crumbs">
                {crumbs.map((c, i) => (
                  <span key={i} className="vault__crumb">
                    {c}
                    {i < crumbs.length - 1 && <span className="vault__sep">/</span>}
                  </span>
                ))}
              </div>
              <div className="vault__head">
                <h2 className="vault__name">{selected.name}</h2>
                {selected.system ? (
                  <Badge>{t("vault.system")}</Badge>
                ) : (
                  <Badge tone="accent">{t("vault.custom")}</Badge>
                )}
              </div>
              {canManage && (
                <div className="vault__actions">
                  <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "create", target: selected })}>
                    {t("vault.newSub")}
                  </Button>
                  {!selected.system && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ mode: "rename", target: selected })}>
                        {t("common.edit")}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setDialog({ mode: "delete", target: selected })}>
                        {t("common.delete")}
                      </Button>
                    </>
                  )}
                </div>
              )}
              <div className="vault__docs">
                <div className="vault__docs-head">
                  <h3 className="vault__docs-t">{t("vault.files")}</h3>
                  {canManage && (
                    <label className="vault__add">
                      <input
                        type="file"
                        hidden
                        disabled={busy}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) void onUpload(f);
                        }}
                      />
                      <span className="ax-btn ax-btn--outline ax-btn--sm">
                        {busy ? t("common.loading") : t("vault.upload")}
                      </span>
                    </label>
                  )}
                </div>

                {filesError ? (
                  <p className="vault__docs-err">{filesError}</p>
                ) : files === null ? (
                  <Spinner />
                ) : files.length === 0 ? (
                  <div className="vault__files">
                    <div className="vault__files-ic">
                      <FolderIcon />
                    </div>
                    <div className="vault__files-t">{t("vault.noFiles")}</div>
                    <div className="vault__files-s">{t("vault.noFilesHint")}</div>
                  </div>
                ) : (
                  <ul className="vault__list">
                    {files.map((f) => (
                      <li key={f.id} className="vault__doc">
                        <div className="vault__doc-main">
                          <a
                            className="vault__doc-name"
                            href={endpoints.vaultFileUrl(f.id)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {f.filename}
                          </a>
                          <div className="vault__doc-meta">
                            <span>{humanSize(f.sizeBytes)}</span>
                            {f.sourceRef && (
                              <span>
                                {f.sourceKind === "register"
                                  ? t("vault.fromRegister", { ref: f.sourceRef })
                                  : t("vault.fromForm", { ref: f.sourceRef })}
                              </span>
                            )}
                            {f.uploaderName && <span>{t("vault.addedBy", { name: f.uploaderName })}</span>}
                          </div>
                        </div>
                        {f.internal && <Badge tone="accent">{t("vault.internal")}</Badge>}
                        {canManage && f.sourceKind === "upload" && (
                          <Button size="sm" variant="ghost" onClick={() => void onRemoveFile(f)}>
                            {t("common.delete")}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <p className="muted">{t("vault.pick")}</p>
          )}
        </Card>
      </div>

      {dialog && (
        <FolderDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onCreate={submitCreate}
          onRename={submitRename}
          onDelete={submitDelete}
        />
      )}
    </div>
  );
}

function FolderDialog({
  dialog,
  onClose,
  onCreate,
  onRename,
  onDelete,
}: {
  dialog: { mode: "create" | "rename" | "delete"; target?: Folder };
  onClose: () => void;
  onCreate: (name: string, parentId: string | null) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(dialog.mode === "rename" ? (dialog.target?.name ?? "") : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run() {
    setBusy(true);
    setError("");
    try {
      if (dialog.mode === "create") await onCreate(name, dialog.target?.id ?? null);
      else if (dialog.mode === "rename" && dialog.target) await onRename(dialog.target.id, name);
      else if (dialog.mode === "delete" && dialog.target) await onDelete(dialog.target.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const title =
    dialog.mode === "create"
      ? t("vault.newFolder")
      : dialog.mode === "rename"
        ? t("vault.rename")
        : t("vault.deleteFolder");

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={dialog.mode === "delete" ? "danger" : "primary"}
            loading={busy}
            onClick={() => void run()}
          >
            {dialog.mode === "delete" ? t("common.delete") : t("common.save")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      {dialog.mode === "delete" ? (
        <p>{t("vault.deleteConfirm", { name: dialog.target?.name ?? "" })}</p>
      ) : (
        <Field
          label={t("vault.folderName")}
          hint={dialog.mode === "create" && dialog.target ? t("vault.inside", { name: dialog.target.name }) : undefined}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
      )}
    </Modal>
  );
}
